// V2 login (BRIEFS C2.4, SPEC §5.1): once per laptop, then signed in for ever.
// Simple words, one "Start" button. Device name = platform · browser (shown to the admin).
import React, { useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useAppAuth } from '../../context/AppAuthContext';
import { CenterMessage } from '../../components/app/AppGate';
import { TitleBar } from '../../components/app/AppLayout';
import { IconLeaf } from '../../components/app/icons';
import { APP_NAME, C, FONT, button } from '../../components/app/theme';

const label = { fontSize: 15, fontWeight: 500, display: 'flex', flexDirection: 'column', gap: 6 };
const input = {
  height: 48, padding: '0 14px', fontSize: 16, fontFamily: FONT.ui, color: C.text,
  border: `1px solid ${C.border}`, borderRadius: 10, background: C.surface, outline: 'none',
};

export default function AppLogin() {
  const { status, login } = useAppAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const goTo = (location.state && location.state.from) || '/app';
  if (status === 'loading') return <CenterMessage title={APP_NAME} text="Opening…" />;
  if (status === 'signedIn') return <Navigate to={goTo.startsWith('/app') ? goTo : '/app'} replace />;

  const submit = async (e) => {
    e.preventDefault();
    if (!username.trim() || !password) { setError('Please type your user name and your password.'); return; }
    setBusy(true);
    setError('');
    try {
      await login(username, password);
      navigate(goTo.startsWith('/app') && goTo !== '/app/login' ? goTo : '/app', { replace: true });
    } catch (err) {
      setError(err && err.response && err.response.status === 401
        ? 'That user name or password is not right. Please try again.'
        : 'We cannot reach the server. Check the internet and try again.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: C.bg, color: C.text, fontFamily: FONT.ui }}>
      <TitleBar />
      <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}>
        <form onSubmit={submit} style={{
          width: '100%', maxWidth: 400, boxSizing: 'border-box', padding: 32,
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 16,
          display: 'flex', flexDirection: 'column', gap: 18,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: C.primary }}>
            <IconLeaf size={26} />
            <div style={{ fontFamily: FONT.heading, fontSize: 22, fontWeight: 600, color: C.text }}>{APP_NAME}</div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <h1 style={{ fontFamily: FONT.heading, margin: 0, fontSize: 26, fontWeight: 600 }}>Welcome</h1>
            <div style={{ fontSize: 15, color: C.muted }}>Sign in one time. This computer will remember you.</div>
          </div>
          <label style={label}>
            User name
            <input style={input} value={username} autoFocus autoComplete="username"
              autoCapitalize="none" spellCheck={false}
              onChange={(e) => setUsername(e.target.value)} name="username" />
          </label>
          <label style={label}>
            Password
            <input style={input} type="password" value={password} autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)} name="password" />
          </label>
          {error && (
            <div role="alert" style={{
              padding: '10px 12px', borderRadius: 8, background: C.badBg, color: C.bad, fontSize: 14,
            }}>{error}</div>
          )}
          <button type="submit" disabled={busy} style={{ ...button.primary, minHeight: 50, opacity: busy ? 0.7 : 1 }}>
            {busy ? 'Please wait…' : 'Start'}
          </button>
        </form>
      </div>
    </div>
  );
}
